<template>
    <div class="prediction-markets">
        <!-- Toast notification -->
        <transition name="fade">
            <div v-if="toast.show" class="predict-toast" :class="'predict-toast--' + toast.type">
                <i :class="toast.type === 'success' ? 'fas fa-check-circle' : toast.type === 'danger' ? 'fas fa-exclamation-circle' : 'fas fa-info-circle'" class="me-2"></i>
                {{ toast.message }}
                <button class="btn-close btn-close-sm ms-auto" @click="toast.show = false" style="font-size: 0.6rem;"></button>
            </div>
        </transition>

        <!-- SSR slot content: Blade HTML rendered for SEO, removed once Vue mounts -->
        <div v-if="showSeoContent" ref="seoContent">
            <slot></slot>
        </div>

        <!-- Filters & View Toggle -->
        <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div class="d-flex gap-2 flex-wrap align-items-center">
                <div class="position-relative" style="min-width: 200px;">
                    <input v-model="searchText" type="text" class="form-control form-control-sm"
                           placeholder="Search markets..." style="padding-left: 2rem;">
                    <i class="fas fa-search position-absolute" style="left: 0.6rem; top: 50%; transform: translateY(-50%); color: #6b7280; font-size: 0.8rem;"></i>
                </div>
                <select v-model="selectedCategory" class="form-select-sm" style="min-width: 140px;">
                    <option value="">All Categories</option>
                    <option v-for="c in categories" :key="c" :value="c">{{ capitalize(c) }}</option>
                </select>
                <select v-model="selectedStatus" class="form-select-sm" style="min-width: 120px;">
                    <option value="active">Active</option>
                    <option value="resolved">Resolved</option>
                    <option value="">All</option>
                </select>
                <select v-model="sortBy" class="form-select-sm" style="min-width: 130px;">
                    <option value="volume">Sort: Volume</option>
                    <option value="yes_price">Sort: Yes Price</option>
                    <option value="end_at">Sort: Ending Soon</option>
                    <option value="newest">Sort: Newest</option>
                </select>
            </div>
            <div class="analytics-tabs">
                <button class="tab-btn" :class="{ active: viewMode === 'grouped' }" @click="viewMode = 'grouped'">
                    <i class="fas fa-layer-group"></i> Events
                </button>
                <button class="tab-btn" :class="{ active: viewMode === 'grid' }" @click="viewMode = 'grid'">
                    <i class="fas fa-th-large"></i> Grid
                </button>
                <button class="tab-btn" :class="{ active: viewMode === 'table' }" @click="viewMode = 'table'">
                    <i class="fas fa-list"></i> Table
                </button>
            </div>
        </div>

        <!-- Your Positions (when wallet connected) -->
        <div v-if="hasPositions" class="compass-card compass-card--accent mb-4">
            <h5 class="mb-3" style="font-weight: 600;">Your Predictions</h5>
            <div class="table-mobile-scroll">
                <table class="compass-table">
                    <thead>
                        <tr>
                            <th>Market</th>
                            <th>Position</th>
                            <th class="text-end">Avg Price</th>
                            <th class="text-end">Current</th>
                            <th class="text-end">P&L</th>
                            <th class="text-end">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="pos in positions" :key="pos.pubkey">
                            <td style="max-width: 250px;">
                                <div style="font-weight: 500;">{{ pos.marketTitle || pos.eventTitle }}</div>
                                <div class="text-muted" style="font-size: 0.75rem;">{{ pos.eventTitle }}</div>
                            </td>
                            <td>
                                <span :class="pos.isYes ? 'text-success' : 'text-danger'" style="font-weight: 600;">
                                    {{ pos.isYes ? 'Yes' : 'No' }} &times;{{ pos.contracts }}
                                </span>
                            </td>
                            <td class="td-num">${{ formatPrice(pos.avgPriceUsd) }}</td>
                            <td class="td-num">${{ formatPrice(pos.markPriceUsd) }}</td>
                            <td class="td-num">
                                <span :class="(pos.unrealizedPnlPct || 0) >= 0 ? 'pct-change pct-positive' : 'pct-change pct-negative'">
                                    {{ (pos.unrealizedPnlPct || 0) >= 0 ? '+' : '' }}{{ formatPct(pos.unrealizedPnlPct) }}
                                </span>
                            </td>
                            <td class="text-end">
                                <button v-if="pos.claimable" class="compass-btn compass-btn--sm compass-btn--primary"
                                        @click="claimPosition(pos)">
                                    Claim
                                </button>
                                <button v-else class="compass-btn compass-btn--sm compass-btn--outline"
                                        @click="sellPosition(pos)">
                                    Sell
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-purple" role="status"></div>
            <p class="mt-2 text-muted">Loading markets...</p>
        </div>

        <!-- Grouped Event View (default) -->
        <div v-else-if="viewMode === 'grouped'" class="prediction-events-grid">
            <div v-for="group in paginatedEvents" :key="group.event_id" class="compass-card">
                <!-- Event Header -->
                <div class="d-flex gap-3 align-items-start mb-3">
                    <div v-if="group.image_url" class="event-icon-wrapper" style="flex-shrink: 0;">
                        <img :src="group.image_url" alt=""
                             class="event-icon">
                    </div>
                    <div v-else class="event-icon-placeholder" style="flex-shrink: 0;">
                        <i class="fas fa-chart-bar"></i>
                    </div>
                    <div class="flex-grow-1">
                        <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
                            <div>
                                <h3 style="font-size: 1.1rem; font-weight: 700; margin: 0; line-height: 1.3;">
                                    <a :href="eventUrl(group)" style="color: inherit; text-decoration: none;">
                                        {{ group.event_title }}
                                    </a>
                                </h3>
                                <div class="d-flex gap-2 align-items-center mt-1 text-muted" style="font-size: 0.8rem;">
                                    <span v-if="group.category" class="compass-badge compass-badge--purple" style="font-size: 0.65rem;">
                                        {{ capitalize(group.category) }}
                                    </span>
                                    <span class="font-mono-num">Vol: {{ group.event_volume_formatted }}</span>
                                    <span v-if="group.days_remaining !== null">{{ group.days_remaining }}d left</span>
                                    <span>{{ group.markets.length }} {{ group.markets.length === 1 ? 'market' : 'outcomes' }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Single market event: Yes/No breakdown + donut -->
                <div v-if="group.markets.length === 1" class="prediction-multi-layout">
                    <div class="prediction-outcomes-chart" @click="goToMarket(group.markets[0])" style="cursor: pointer;">
                        <div class="outcome-row">
                            <div class="outcome-label">
                                <span>Yes</span>
                            </div>
                            <div class="outcome-bar-container">
                                <div class="outcome-bar outcome-bar--yes"
                                     :style="{ width: Math.max(2, group.markets[0].yes_price * 100) + '%' }">
                                </div>
                                <span class="outcome-value font-mono-num" style="color: var(--compass-success);">
                                    {{ formatPct(group.markets[0].yes_price * 100) }}
                                </span>
                            </div>
                        </div>
                        <div class="outcome-row">
                            <div class="outcome-label">
                                <span>No</span>
                            </div>
                            <div class="outcome-bar-container">
                                <div class="outcome-bar outcome-bar--no"
                                     :style="{ width: Math.max(2, group.markets[0].no_price * 100) + '%' }">
                                </div>
                                <span class="outcome-value font-mono-num" style="color: var(--compass-danger);">
                                    {{ formatPct(group.markets[0].no_price * 100) }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="prediction-donut-panel">
                        <canvas :ref="el => initYesNoDonut(el, group)" width="160" height="160"></canvas>
                        <div class="donut-leader">
                            <span class="donut-leader-name" :style="{ color: group.markets[0].yes_price >= 0.5 ? 'var(--compass-success)' : 'var(--compass-danger)' }">
                                {{ group.markets[0].yes_price >= 0.5 ? 'Yes' : 'No' }}
                            </span>
                            <span class="donut-leader-pct font-mono-num" :style="{ color: group.markets[0].yes_price >= 0.5 ? 'var(--compass-success)' : 'var(--compass-danger)' }">
                                {{ formatPct(Math.max(group.markets[0].yes_price, group.markets[0].no_price) * 100) }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Multi-market event: breakdown + donut chart -->
                <div v-else class="prediction-multi-layout">
                    <div class="prediction-outcomes-chart">
                        <div v-for="m in group.topMarkets" :key="m.market_id || m.id"
                             class="outcome-row" @click="goToMarket(m)">
                            <div class="outcome-label" :title="m.market_title">
                                <img v-if="m.market_image_url" :src="m.market_image_url" alt=""
                                     class="outcome-avatar">
                                <span>{{ m.market_title }}</span>
                            </div>
                            <div class="outcome-bar-container">
                                <div class="outcome-bar"
                                     :style="{ width: Math.max(2, m.yes_price * 100) + '%' }">
                                </div>
                                <span class="outcome-value font-mono-num">{{ formatPct(m.yes_price * 100) }}</span>
                            </div>
                        </div>
                        <a v-if="group.markets.length > 6"
                           :href="'/app/predict/event/' + group.event_id"
                           style="font-size: 0.8rem; color: var(--compass-purple); text-decoration: none; display: block; text-align: right; margin-top: 4px;">
                            +{{ group.markets.length - 6 }} more outcomes &rarr;
                        </a>
                    </div>
                    <div class="prediction-donut-panel">
                        <canvas :ref="el => initDonut(el, group)" width="160" height="160"></canvas>
                        <div class="donut-leader">
                            <span class="donut-leader-name">{{ group.topMarkets[0]?.market_title }}</span>
                            <span class="donut-leader-pct font-mono-num">{{ formatPct(group.topMarkets[0]?.yes_price * 100) }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="!paginatedEvents.length" class="prediction-grid-full text-center py-4 text-muted">
                No markets match your filters.
            </div>
            <div v-if="hasMore" ref="scrollSentinel" class="prediction-grid-full text-center py-3">
                <div class="spinner-border spinner-border-sm text-purple" role="status"></div>
                <span class="ms-2 text-muted" style="font-size: 0.85rem;">Loading more events...</span>
            </div>
        </div>

        <!-- Grid View: Events (not individual outcomes) -->
        <div v-else-if="viewMode === 'grid'" class="row">
            <div v-for="group in paginatedEvents" :key="group.event_id"
                 class="col-lg-4 col-md-6 mb-3">
                <a :href="eventUrl(group)" class="predict-grid-card h-100 d-block"
                   style="text-decoration: none; color: inherit;">
                    <div class="d-flex gap-2 align-items-start mb-2">
                        <div v-if="group.image_url" class="event-icon-wrapper event-icon-wrapper--sm" style="flex-shrink: 0;">
                            <img :src="group.image_url" alt="" class="event-icon event-icon--sm">
                        </div>
                        <div v-else class="event-icon-placeholder event-icon-placeholder--sm" style="flex-shrink: 0;">
                            <i class="fas fa-chart-bar"></i>
                        </div>
                        <div class="flex-grow-1" style="min-width: 0;">
                            <div style="font-weight: 600; font-size: 0.95rem; line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">
                                {{ group.event_title }}
                            </div>
                            <div class="text-muted mt-1 d-flex align-items-center gap-1 flex-wrap" style="font-size: 0.75rem;">
                                <span v-if="group.category" class="compass-badge compass-badge--purple" style="font-size: 0.6rem; padding: 1px 6px;">
                                    {{ capitalize(group.category) }}
                                </span>
                                <span v-if="group.days_remaining !== null">
                                    {{ group.days_remaining }}d left
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Single market: odds bar -->
                    <div v-if="group.markets.length === 1" class="mt-auto mb-2 pt-2">
                        <div style="display: flex; height: 10px; border-radius: 5px; overflow: hidden;">
                            <div :style="{ width: (group.markets[0].yes_price * 100) + '%', background: 'var(--compass-success)' }"></div>
                            <div :style="{ width: (group.markets[0].no_price * 100) + '%', background: 'var(--compass-danger)' }"></div>
                        </div>
                        <div class="d-flex justify-content-between mt-1" style="font-size: 0.8rem;">
                            <span style="color: var(--compass-success); font-weight: 600;">
                                Yes {{ formatPct(group.markets[0].yes_price * 100) }}
                            </span>
                            <span style="color: var(--compass-danger); font-weight: 600;">
                                No {{ formatPct(group.markets[0].no_price * 100) }}
                            </span>
                        </div>
                    </div>

                    <!-- Multi-market: top 3 outcomes mini bar -->
                    <div v-else class="mt-auto mb-2 pt-2">
                        <div v-for="m in group.topMarkets.slice(0, 3)" :key="m.market_id || m.id"
                             class="d-flex align-items-center gap-2 mb-1" style="font-size: 0.8rem;">
                            <img v-if="m.market_image_url" :src="m.market_image_url" alt=""
                                 style="width: 18px; height: 18px; border-radius: 50%; object-fit: cover; flex-shrink: 0;">
                            <span style="flex: 0 1 auto; min-width: 60px; max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 0.78rem;">
                                {{ m.market_title }}
                            </span>
                            <div style="flex: 1; height: 6px; background: rgba(139,92,246,0.1); border-radius: 3px; overflow: hidden;">
                                <div :style="{ width: Math.max(2, m.yes_price * 100) + '%', background: 'var(--compass-purple)', height: '100%', borderRadius: '3px' }"></div>
                            </div>
                            <span class="font-mono-num" style="font-weight: 600; color: var(--compass-purple); min-width: 38px; text-align: right; font-size: 0.75rem;">
                                {{ formatPct(m.yes_price * 100) }}
                            </span>
                        </div>
                        <div v-if="group.markets.length > 3" class="analytics-text-subtle" style="font-size: 0.7rem; text-align: right;">
                            +{{ group.markets.length - 3 }} more
                        </div>
                    </div>

                    <div class="d-flex justify-content-between align-items-center pt-2"
                         style="border-top: 1px solid rgba(139, 92, 246, 0.1); font-size: 0.78rem; color: #6b7280;">
                        <span class="font-mono-num">Vol: {{ group.event_volume_formatted }}</span>
                        <span>{{ group.markets.length }} {{ group.markets.length === 1 ? 'market' : 'outcomes' }}</span>
                    </div>
                </a>
            </div>
            <div v-if="!paginatedEvents.length" class="col-12 text-center py-4 text-muted">
                No events match your filters.
            </div>
            <div v-if="hasMore" ref="scrollSentinel" class="col-12 text-center py-3">
                <div class="spinner-border spinner-border-sm text-purple" role="status"></div>
                <span class="ms-2 text-muted" style="font-size: 0.85rem;">Loading more events...</span>
            </div>
        </div>

        <!-- Table View: Events (not individual outcomes) -->
        <div v-else class="table-mobile-scroll">
            <table class="compass-table">
                <thead>
                    <tr>
                        <th>Event</th>
                        <th>Category</th>
                        <th class="text-end">Outcomes</th>
                        <th class="text-end">Top Outcome</th>
                        <th class="text-end" style="cursor: pointer;" @click="sortBy = 'volume'">
                            Volume <i v-if="sortBy === 'volume'" class="fas fa-sort-down"></i>
                        </th>
                        <th class="text-end" style="cursor: pointer;" @click="sortBy = 'end_at'">
                            Ends <i v-if="sortBy === 'end_at'" class="fas fa-sort-down"></i>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="group in paginatedEvents" :key="group.event_id">
                        <td style="max-width: 300px;">
                            <a :href="eventUrl(group)" style="color: inherit; text-decoration: none;">
                                <div class="d-flex align-items-center gap-2">
                                    <div v-if="group.image_url" class="event-icon-wrapper event-icon-wrapper--xs">
                                        <img :src="group.image_url" alt="" class="event-icon event-icon--xs">
                                    </div>
                                    <span style="font-weight: 500;">{{ group.event_title }}</span>
                                </div>
                            </a>
                        </td>
                        <td>
                            <span v-if="group.category" class="compass-badge compass-badge--purple" style="font-size: 0.7rem;">
                                {{ capitalize(group.category) }}
                            </span>
                        </td>
                        <td class="td-num">{{ group.markets.length }}</td>
                        <td class="td-num">
                            <div v-if="group.markets.length === 1">
                                <span style="color: var(--compass-success); font-weight: 600;">
                                    {{ formatPct(group.markets[0].yes_price * 100) }}
                                </span>
                                <span style="color: #d1d5db;">/</span>
                                <span style="color: var(--compass-danger);">
                                    {{ formatPct(group.markets[0].no_price * 100) }}
                                </span>
                            </div>
                            <div v-else style="font-size: 0.85rem;">
                                <span style="font-weight: 500;">{{ group.topMarkets[0]?.market_title }}</span>
                                <span class="ms-1" style="color: var(--compass-purple); font-weight: 600;">
                                    {{ formatPct(group.topMarkets[0]?.yes_price * 100) }}
                                </span>
                            </div>
                        </td>
                        <td class="td-num">{{ group.event_volume_formatted }}</td>
                        <td class="td-num" style="font-size: 0.85rem;">
                            {{ group.end_at ? formatDate(group.end_at) : '—' }}
                        </td>
                    </tr>
                    <tr v-if="!paginatedEvents.length">
                        <td colspan="6" class="text-center text-muted">No events match your filters.</td>
                    </tr>
                </tbody>
            </table>
            <div v-if="hasMore" ref="scrollSentinel" class="text-center py-3">
                <div class="spinner-border spinner-border-sm text-purple" role="status"></div>
                <span class="ms-2 text-muted" style="font-size: 0.85rem;">Loading more events...</span>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { useSolSdk } from '../composables/useSolSdk';
import { formatPct, formatPrice, formatVolume, capitalize } from '../composables/usePredictFormatters';
import { Chart, DoughnutController, ArcElement, Tooltip } from 'chart.js';

Chart.register(DoughnutController, ArcElement, Tooltip);

const DONUT_COLORS = [
    '#8b5cf6', '#ec4899', '#10f7cf', '#f59e0b', '#3b82f6', '#22c55e',
    '#6b7280', // "Others" slice
];

export default {
    name: 'PredictionMarkets',
    props: {
        initialMarkets: { type: Array, default: () => [] },
        categories: { type: Array, default: () => [] },
        currentCategory: { type: String, default: '' },
    },
    setup(props) {
        const { getSdk } = useSolSdk();
        const donutCharts = new Map();

        const showSeoContent = ref(true);
        const seoContent = ref(null);

        const markets = ref(props.initialMarkets);
        const loading = ref(false);
        const viewMode = ref('grouped');
        const searchText = ref('');
        const selectedCategory = ref(props.currentCategory || '');
        const selectedStatus = ref('active');
        const sortBy = ref('volume');

        const scrollSentinel = ref(null);
        let scrollObserver = null;

        const visibleCount = ref(12);

        const positions = ref([]);
        const hasPositions = computed(() => positions.value.length > 0);

        // Toast notification for inline feedback
        const toast = ref({ show: false, message: '', type: 'success' });
        let toastTimer = null;
        function showToast(message, type = 'success') {
            if (toastTimer) clearTimeout(toastTimer);
            toast.value = { show: true, message, type };
            toastTimer = setTimeout(() => { toast.value.show = false; }, 5000);
        }

        const filteredMarkets = computed(() => {
            let result = [...markets.value];

            if (selectedStatus.value) {
                result = result.filter(m => m.status === selectedStatus.value);
            }

            if (selectedCategory.value) {
                result = result.filter(m => m.category === selectedCategory.value);
            }

            if (searchText.value) {
                const q = searchText.value.toLowerCase();
                result = result.filter(m =>
                    (m.market_title || '').toLowerCase().includes(q) ||
                    (m.event_title || '').toLowerCase().includes(q)
                );
            }

            result.sort((a, b) => {
                if (sortBy.value === 'volume') return (b.event_volume_usd || b.volume_usd || 0) - (a.event_volume_usd || a.volume_usd || 0);
                if (sortBy.value === 'yes_price') return (b.yes_price || 0) - (a.yes_price || 0);
                if (sortBy.value === 'end_at') {
                    const aDate = a.end_at ? new Date(a.end_at).getTime() : Infinity;
                    const bDate = b.end_at ? new Date(b.end_at).getTime() : Infinity;
                    return aDate - bDate;
                }
                if (sortBy.value === 'newest') {
                    const aDate = a.begin_at ? new Date(a.begin_at).getTime() : 0;
                    const bDate = b.begin_at ? new Date(b.begin_at).getTime() : 0;
                    return bDate - aDate;
                }
                return 0;
            });

            return result;
        });

        // Group markets by event_id for all views
        const groupedEvents = computed(() => {
            const filtered = filteredMarkets.value;
            const groups = {};

            for (const m of filtered) {
                const eid = m.event_id;
                if (!groups[eid]) {
                    groups[eid] = {
                        event_id: eid,
                        event_title: m.event_title,
                        category: m.category,
                        image_url: m.image_url,
                        event_volume_usd: m.event_volume_usd || m.volume_usd || 0,
                        event_volume_formatted: m.event_volume_formatted || formatVolume(m.event_volume_usd || m.volume_usd),
                        days_remaining: m.days_remaining,
                        end_at: m.end_at,
                        markets: [],
                    };
                }
                groups[eid].markets.push(m);
            }

            // Sort markets within each group by yes_price desc, compute topMarkets
            const result = Object.values(groups).filter(g => g.markets.length > 0);
            for (const g of result) {
                g.markets.sort((a, b) => (b.yes_price || 0) - (a.yes_price || 0));
                g.topMarkets = g.markets.slice(0, 6);
            }

            // Sort groups
            if (sortBy.value === 'volume') {
                result.sort((a, b) => b.event_volume_usd - a.event_volume_usd);
            } else if (sortBy.value === 'end_at') {
                result.sort((a, b) => {
                    const aDate = a.end_at ? new Date(a.end_at).getTime() : Infinity;
                    const bDate = b.end_at ? new Date(b.end_at).getTime() : Infinity;
                    return aDate - bDate;
                });
            } else if (sortBy.value === 'newest') {
                result.sort((a, b) => {
                    const aDate = a.markets[0]?.begin_at ? new Date(a.markets[0].begin_at).getTime() : 0;
                    const bDate = b.markets[0]?.begin_at ? new Date(b.markets[0].begin_at).getTime() : 0;
                    return bDate - aDate;
                });
            } else if (sortBy.value === 'yes_price') {
                // For yes_price sort, sort by top outcome's yes_price
                result.sort((a, b) => (b.topMarkets[0]?.yes_price || 0) - (a.topMarkets[0]?.yes_price || 0));
            }

            return result;
        });

        const paginatedEvents = computed(() => groupedEvents.value.slice(0, visibleCount.value));
        const hasMore = computed(() => groupedEvents.value.length > visibleCount.value || apiHasMore);
        const remainingCount = computed(() => {
            const localRemaining = groupedEvents.value.length - visibleCount.value;
            return localRemaining > 0 ? localRemaining : (apiHasMore ? 12 : 0);
        });

        function showMore() {
            visibleCount.value += 12;
            // If we're near the end of loaded data and the API has more pages, fetch next page
            if (visibleCount.value >= groupedEvents.value.length - 6 && apiHasMore && !loadingMore.value) {
                loadingMore.value = true;
                apiPage++;
                refreshMarkets(true);
            }
        }

        function eventUrl(group) {
            if (group.markets.length === 1) {
                return '/app/predict/' + group.markets[0].slug;
            }
            return '/app/predict/event/' + group.event_id;
        }



        function formatDate(dateStr) {
            if (!dateStr) return '—';
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        }

        function goToMarket(market) {
            window.location.href = '/app/predict/' + market.slug;
        }

        function initYesNoDonut(canvas, group) {
            const key = 'yesno-' + group.event_id;
            if (!canvas || donutCharts.has(key)) return;
            donutCharts.set(key, true);

            nextTick(() => {
                const m = group.markets[0];
                new Chart(canvas, {
                    type: 'doughnut',
                    data: {
                        labels: ['Yes', 'No'],
                        datasets: [{
                            data: [m.yes_price || 0, m.no_price || 0],
                            backgroundColor: ['#22c55e', '#ef4444'],
                            borderWidth: 0,
                            hoverOffset: 4,
                        }],
                    },
                    options: {
                        responsive: false,
                        cutout: '60%',
                        plugins: {
                            legend: { display: false },
                            tooltip: {
                                callbacks: {
                                    label: (ctx) => `${ctx.label}: ${(ctx.parsed * 100).toFixed(1)}%`,
                                },
                            },
                        },
                    },
                });
            });
        }

        function initDonut(canvas, group) {
            if (!canvas || donutCharts.has(group.event_id)) return;
            donutCharts.set(group.event_id, true);

            nextTick(() => {
                const top = group.topMarkets.slice(0, 6);
                const topTotal = top.reduce((s, m) => s + (m.yes_price || 0), 0);
                const othersTotal = Math.max(0, 1 - topTotal);

                const labels = top.map(m => m.market_title);
                const data = top.map(m => m.yes_price || 0);
                const colors = top.map((_, i) => DONUT_COLORS[i % DONUT_COLORS.length]);

                if (othersTotal > 0.01) {
                    labels.push('Others');
                    data.push(othersTotal);
                    colors.push(DONUT_COLORS[6]);
                }

                new Chart(canvas, {
                    type: 'doughnut',
                    data: {
                        labels,
                        datasets: [{
                            data,
                            backgroundColor: colors,
                            borderWidth: 0,
                            hoverOffset: 4,
                        }],
                    },
                    options: {
                        responsive: false,
                        cutout: '60%',
                        plugins: {
                            legend: { display: false },
                            tooltip: {
                                callbacks: {
                                    label: (ctx) => `${ctx.label}: ${(ctx.parsed * 100).toFixed(1)}%`,
                                },
                            },
                        },
                    },
                });
            });
        }

        let apiPage = 1;
        let apiHasMore = false;
        let loadingMore = ref(false);
        let initialFilters = true; // skip first API call since we have SSR data

        async function refreshMarkets(append = false) {
            try {
                const params = new URLSearchParams();
                if (selectedCategory.value) params.set('category', selectedCategory.value);
                if (selectedStatus.value) params.set('status', selectedStatus.value);
                if (searchText.value) params.set('search', searchText.value);
                params.set('page', String(apiPage));
                params.set('per_page', '500');

                const resp = await fetch('/api/predict/markets?' + params.toString());
                const json = await resp.json();
                if (json.data) {
                    if (append) {
                        markets.value = [...markets.value, ...json.data];
                    } else {
                        markets.value = json.data;
                    }
                }
                apiHasMore = json.meta?.has_more ?? false;
            } catch (e) {
                console.error('[PredictionMarkets] refresh failed:', e);
            } finally {
                loadingMore.value = false;
            }
        }

        async function loadPositions() {
            const wallet = window.wallet;
            const pubkey = wallet?.publicKey?.value;
            if (!pubkey) {
                positions.value = [];
                return;
            }

            try {
                const sdk = await getSdk();
                const result = await sdk.predict.getPositions(pubkey.toBase58());
                positions.value = result || [];
            } catch (e) {
                console.error('[PredictionMarkets] Failed to load positions:', e);
                positions.value = [];
            }
        }

        async function claimPosition(pos) {
            const wallet = window.wallet;
            const pubkey = wallet?.publicKey?.value;
            if (!pubkey) {
                showToast('Please connect your wallet first.', 'warning');
                return;
            }

            try {
                const sdk = await getSdk();
                const result = await sdk.predict.claim(pubkey.toBase58(), pos.pubkey);
                showToast(`Claimed $${Number(result.payoutUsd).toFixed(2)} USDC!`, 'success');
                loadPositions();
            } catch (e) {
                console.error('[PredictionMarkets] Claim failed:', e);
                showToast('Claim failed: ' + (e.message || 'Unknown error'), 'danger');
            }
        }

        async function sellPosition(pos) {
            const wallet = window.wallet;
            const pubkey = wallet?.publicKey?.value;
            if (!pubkey) {
                showToast('Please connect your wallet first.', 'warning');
                return;
            }

            try {
                const sdk = await getSdk();
                const result = await sdk.predict.sell(pubkey.toBase58(), pos.pubkey);
                showToast(`Sold for $${Number(result.proceedsUsd).toFixed(2)} USDC (P&L: $${Number(result.realizedPnlUsd).toFixed(2)})`, 'success');
                loadPositions();
            } catch (e) {
                console.error('[PredictionMarkets] Sell failed:', e);
                showToast('Sell failed: ' + (e.message || 'Unknown error'), 'danger');
            }
        }

        // Reset pagination when filters change; fetch from API if user changed filters
        watch([searchText, selectedCategory, selectedStatus, sortBy], () => {
            visibleCount.value = 12;
            if (initialFilters) {
                initialFilters = false;
                return;
            }
            apiPage = 1;
            refreshMarkets();
        });

        function setupScrollObserver() {
            if (scrollObserver) scrollObserver.disconnect();
            nextTick(() => {
                if (!scrollSentinel.value) return;
                scrollObserver = new IntersectionObserver((entries) => {
                    if (entries[0]?.isIntersecting && hasMore.value) {
                        showMore();
                        // Re-observe after DOM update for new sentinel position
                        nextTick(() => {
                            if (scrollSentinel.value) {
                                scrollObserver.disconnect();
                                scrollObserver.observe(scrollSentinel.value);
                            }
                        });
                    }
                }, { rootMargin: '200px' });
                scrollObserver.observe(scrollSentinel.value);
            });
        }

        onMounted(() => {
            // Remove SSR slot content — Vue interactive UI takes over
            showSeoContent.value = false;

            setupScrollObserver();

            if (window.wallet?.publicKey?.value) {
                loadPositions();
            }
        });

        onBeforeUnmount(() => {
            if (scrollObserver) scrollObserver.disconnect();
        });

        // Re-setup observer when view mode or data changes
        watch([viewMode, groupedEvents], () => {
            nextTick(setupScrollObserver);
        });

        if (window.wallet?.publicKey) {
            watch(() => window.wallet.publicKey.value, (newVal) => {
                if (newVal) loadPositions();
                else positions.value = [];
            });
        }

        return {
            showSeoContent,
            seoContent,
            scrollSentinel,
            markets,
            loading,
            viewMode,
            searchText,
            selectedCategory,
            selectedStatus,
            sortBy,
            filteredMarkets,
            groupedEvents,
            paginatedEvents,
            hasMore,
            remainingCount,
            showMore,
            positions,
            hasPositions,
            capitalize,
            formatPct,
            formatPrice,
            formatVolume,
            formatDate,
            goToMarket,
            eventUrl,
            initDonut,
            initYesNoDonut,
            claimPosition,
            sellPosition,
            toast,
        };
    },
};
</script>

<style scoped>
.predict-toast {
    position: sticky;
    top: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    font-size: 0.85rem;
    margin-bottom: 1rem;
    font-weight: 500;
}
.predict-toast--success {
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.3);
    color: var(--compass-success, #10b981);
}
.predict-toast--danger {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: var(--compass-danger, #ef4444);
}
.predict-toast--warning {
    background: rgba(245, 158, 11, 0.1);
    border: 1px solid rgba(245, 158, 11, 0.3);
    color: #f59e0b;
}
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
    opacity: 0;
}
.predict-grid-card {
    display: flex;
    flex-direction: column;
    padding: 1.25rem;
    background: rgba(139, 92, 246, 0.03);
    border: 1px solid rgba(139, 92, 246, 0.12);
    border-radius: var(--compass-radius, 12px);
    transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
    cursor: pointer;
}

.predict-grid-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 16px rgba(139, 92, 246, 0.12);
    border-color: rgba(139, 92, 246, 0.3);
}

.dark-mode .predict-grid-card {
    background: rgba(139, 92, 246, 0.06);
    border-color: rgba(139, 92, 246, 0.15);
}

.dark-mode .predict-grid-card:hover {
    border-color: rgba(139, 92, 246, 0.35);
    box-shadow: 0 4px 16px rgba(139, 92, 246, 0.15);
}

.prediction-outcomes-chart {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.outcome-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 4px 8px;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.15s;
}

.outcome-row:hover {
    background: rgba(139, 92, 246, 0.06);
}

.outcome-label {
    width: 160px;
    flex-shrink: 0;
    font-size: 0.85rem;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    align-items: center;
    gap: 8px;
}

.outcome-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
}

.outcome-bar-container {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 24px;
}

.outcome-bar {
    height: 100%;
    background: linear-gradient(90deg, var(--compass-purple), rgba(139, 92, 246, 0.6));
    border-radius: 4px;
    min-width: 4px;
    transition: width 0.3s ease;
}

.outcome-bar--yes {
    background: linear-gradient(90deg, var(--compass-success), rgba(34, 197, 94, 0.6)) !important;
}

.outcome-bar--no {
    background: linear-gradient(90deg, var(--compass-danger), rgba(239, 68, 68, 0.6)) !important;
}

.outcome-value {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--compass-purple);
    min-width: 50px;
    text-align: right;
}

/* Two-column grid for events view */
.prediction-events-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
}

.prediction-grid-full {
    grid-column: 1 / -1;
}

/* In 2-col grid, shrink donut for narrower cards */
.prediction-events-grid .prediction-donut-panel canvas {
    width: 120px !important;
    height: 120px !important;
}

@media (max-width: 992px) {
    .prediction-events-grid {
        grid-template-columns: 1fr;
    }
}

/* Two-panel layout: bars on left, donut on right */
.prediction-multi-layout {
    display: flex;
    gap: 24px;
    align-items: flex-start;
}

.prediction-multi-layout > .prediction-outcomes-chart {
    flex: 1;
    min-width: 0;
}

.prediction-donut-panel {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
}

.donut-leader {
    text-align: center;
    max-width: 160px;
}

.donut-leader-name {
    display: block;
    font-size: 0.78rem;
    font-weight: 500;
    color: var(--compass-purple);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
}

.donut-leader-pct {
    display: block;
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--compass-purple);
}

@media (max-width: 768px) {
    .prediction-multi-layout {
        flex-direction: column;
    }

    .prediction-donut-panel {
        flex-direction: row;
        justify-content: center;
        gap: 12px;
        width: 100%;
    }

    .prediction-donut-panel canvas {
        width: 100px !important;
        height: 100px !important;
    }
}

/* Event icon with background so transparent PNGs are visible */
.event-icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.12), rgba(236, 72, 153, 0.08));
    border: 1px solid rgba(139, 92, 246, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(139, 92, 246, 0.1);
}

.event-icon-wrapper--sm {
    width: 40px;
    height: 40px;
}

.event-icon-wrapper--xs {
    width: 28px;
    height: 28px;
    border-radius: 6px;
}

.event-icon {
    width: 40px;
    height: 40px;
    object-fit: contain;
}

.event-icon--sm {
    width: 32px;
    height: 32px;
}

.event-icon--xs {
    width: 22px;
    height: 22px;
}

.event-icon-placeholder {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.1));
    border: 1px solid rgba(139, 92, 246, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--compass-purple);
    font-size: 1.1rem;
}

.event-icon-placeholder--sm {
    width: 40px;
    height: 40px;
    font-size: 1rem;
}

.dark-mode .event-icon-wrapper {
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(236, 72, 153, 0.12));
    border-color: rgba(139, 92, 246, 0.2);
}

.dark-mode .event-icon-placeholder {
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(236, 72, 153, 0.12));
    border-color: rgba(139, 92, 246, 0.2);
}

@media (max-width: 768px) {
    .outcome-label {
        width: 110px;
        font-size: 0.8rem;
    }

    .outcome-value {
        font-size: 0.75rem;
        min-width: 40px;
    }

    .outcome-row {
        gap: 8px;
        padding: 3px 4px;
    }

    .outcome-bar-container {
        gap: 6px;
    }
}
</style>
